import {
  postWithAuth,
  getWithAuth,
  fetchWithAuth,
  postStreamWithAuth,
} from '@/lib/httpAdapter';
import apiUrl from './api';
import { normalizeUrl } from '@/lib/utils/url';
import type {
  BoardDetailDTO,
  ListBoardsResponseDTO,
  BoardMessageDTO,
} from '@/types/boards';

const BASE = normalizeUrl(apiUrl, 'api/ai-chat');

type Ctx = { orgUUID: string; userUUID: string };

async function asJson<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${label} failed (${res.status}): ${text}`);
  }
  const data = await res.json();
  return (typeof data === 'string' ? JSON.parse(data) : data) as T;
}

// ── Board CRUD ──────────────────────────────────────────────────────────────
export async function listBoards(ctx: Ctx): Promise<ListBoardsResponseDTO> {
  const p = new URLSearchParams({
    orgUUID: ctx.orgUUID,
    userUUID: ctx.userUUID,
    limit: '100',
  });
  return asJson(await getWithAuth(`${BASE}/boards/?${p}`), 'listBoards');
}

export async function createBoard(
  req: Ctx & { title?: string; author?: string; uuid?: string; prompt?: string }
): Promise<BoardDetailDTO> {
  return asJson(await postWithAuth(`${BASE}/boards/`, req), 'createBoard');
}

export async function getBoard(
  req: Ctx & { boardUUID: string }
): Promise<BoardDetailDTO> {
  const p = new URLSearchParams({
    orgUUID: req.orgUUID,
    userUUID: req.userUUID,
  });
  return asJson(
    await getWithAuth(`${BASE}/boards/${req.boardUUID}/?${p}`),
    'getBoard'
  );
}

export async function updateBoard(
  req: Ctx & {
    boardUUID: string;
    title?: string;
    layouts?: Record<string, unknown>;
    messages?: BoardMessageDTO[];
    artifactContent?: string;
  }
): Promise<BoardDetailDTO> {
  return asJson(
    await fetchWithAuth(`${BASE}/boards/${req.boardUUID}/`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }),
    'updateBoard'
  );
}

export async function deleteBoard(
  req: Ctx & { boardUUID: string }
): Promise<void> {
  const p = new URLSearchParams({
    orgUUID: req.orgUUID,
    userUUID: req.userUUID,
  });
  const res = await fetchWithAuth(`${BASE}/boards/${req.boardUUID}/?${p}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`deleteBoard failed (${res.status})`);
}

export async function duplicateBoard(
  req: Ctx & { boardUUID: string }
): Promise<BoardDetailDTO> {
  return asJson(
    await postWithAuth(`${BASE}/boards/${req.boardUUID}/duplicate/`, req),
    'duplicateBoard'
  );
}

// ── Board generation (c1app artifact + live Query() data via toolProvider) ────
// Streams the whole board as a c1app artifact string from POST /board/. The
// artifact embeds Query() tool calls (name + params) which the C1Component
// resolves live on the client via callBoardC1appTool — so a reload re-renders
// the persisted artifact with fresh data and NO model call.
export interface BoardMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function streamBoardC1app(
  req: Ctx & {
    messages: BoardMessage[];
    threadId: string;
    title?: string;
  },
  signal?: AbortSignal
): Promise<Response> {
  const res = await postStreamWithAuth(`${BASE}/board/`, req, { signal });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res;
}

export interface BoardToolResult {
  content: Array<{ type: string; text?: string }>;
  structuredContent?: unknown;
  isError?: boolean;
}

// Resolves one Query() tool call for the C1Component's tool runtime. Returns the
// FULL { content, structuredContent } envelope — the C1Component MCP toolProvider
// expects the whole envelope and unwraps it itself; do NOT unwrap here.
export async function callBoardC1appTool(
  req: Ctx & {
    name: string;
    arguments: Record<string, unknown>;
    // Board UUID — lets the backend nest this data fetch under the board's
    // observability session. Optional; omitting it has no functional effect.
    threadId?: string;
  }
): Promise<BoardToolResult> {
  return asJson(
    await postWithAuth(`${BASE}/board/tool/`, req),
    'callBoardC1appTool'
  );
}

// ── Custom dashboard: per-section signal analysis ────────────────────────────
// The custom (AI-generated) dashboard analyzes ONE Team Insights / Code Security
// section's live data and returns the key signals an engineering leader should
// act on. The FE renders these as a signals strip and seeds the board prompt
// with them so the generated dashboard leads with them. Resolved by the backend
// POST /api/ai-chat/board/signals/ (sweeps the section's board tools + LLM).
export interface BoardSignal {
  id: string;
  title: string;
  severity: 'critical' | 'warning' | 'info' | 'positive';
  summary: string;
  metric: string;
  value: string;
  trend: 'up' | 'down' | 'flat' | null;
  recommendation: string;
}

export interface BoardSignalsResponse {
  success: boolean;
  section: string;
  signals: BoardSignal[];
}

export async function fetchBoardSignals(
  req: Ctx & { section: string; range?: string; timezone?: string }
): Promise<BoardSignalsResponse> {
  const res = await postWithAuth(`${BASE}/board/signals/`, req);
  // 404 = this backend has no signals route: no signals, not an error.
  if (res.status === 404) {
    return { success: true, section: req.section, signals: [] };
  }
  return asJson(res, 'fetchBoardSignals');
}

// ─── Named-SSE progress events (board multi-agent planner + designer) ────────
export type BoardThinkingAgent = 'researcher' | 'data_scout' | 'designer';

export interface BoardPhaseEvent {
  event: 'phase';
  data: { agent: BoardThinkingAgent; label: string; state: 'running' | 'done' };
}
export interface BoardStepEvent {
  event: 'step';
  data: {
    agent: BoardThinkingAgent;
    text: string;
    detail?: string;
    // 'thinking' = the orchestrator's live reasoning (rendered muted/italic).
    state?: 'running' | 'thinking' | 'done';
  };
}
export interface BoardArtifactFinalEvent {
  event: 'artifact_final';
  data: { artifact: string };
}
export interface BoardErrorEvent {
  event: 'error';
  data: { code: string; error: string };
}
export type BoardSSEEvent =
  | BoardPhaseEvent
  | BoardStepEvent
  | BoardArtifactFinalEvent
  | BoardErrorEvent;

/**
 * Parse accumulated SSE text into complete named events, returning any
 * incomplete trailing chunk in `rest` to be prepended to the next read. Mirrors
 * the buffering pattern in `apis/backfillMetrics.ts`. Malformed blocks are
 * skipped (logged by the caller if needed).
 */
export function parseBoardSSE(buffer: string): {
  events: BoardSSEEvent[];
  rest: string;
} {
  const events: BoardSSEEvent[] = [];
  // Events are separated by a blank line ("\n\n"). The last segment may be
  // incomplete -> keep it as `rest`.
  const segments = buffer.split('\n\n');
  const rest = segments.pop() ?? '';
  for (const seg of segments) {
    const lines = seg.split('\n');
    let evName = '';
    let dataStr = '';
    for (const line of lines) {
      if (line.startsWith('event: ')) evName = line.slice(7).trim();
      else if (line.startsWith('data: ')) dataStr += line.slice(6);
    }
    if (!evName || !dataStr) continue;
    try {
      events.push({
        event: evName,
        data: JSON.parse(dataStr),
      } as BoardSSEEvent);
    } catch {
      // Skip malformed event; caller continues.
    }
  }
  return { events, rest };
}
